import axios from 'axios';
import { clearAiStudioSessions } from '../utils/haircutsStudioSession';

const instance = axios.create({
  // Default to same-origin in production so the browser calls the same host that served the frontend.
  // Override with REACT_APP_API_URL for local development or custom deployments.
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

// ---------------------------------------------------------------------------
// Session-timeout integration hooks
//
// AuthContext registers these two callbacks after it mounts so axiosClient can
// drive session state without importing React context (avoids circular deps).
// Both default to null so the module is safe before the provider mounts.
// ---------------------------------------------------------------------------
let _activityResetter  = null;  // () => void — fires on every user-triggered request
let _sessionLogoutOnce = null;  // () => void — fires on SESSION_EXPIRED 401

export const setActivityResetter  = (fn) => { _activityResetter  = fn; };
export const setSessionLogoutOnce = (fn) => { _sessionLogoutOnce = fn; };

// ---------------------------------------------------------------------------
// Request interceptor
// ---------------------------------------------------------------------------
instance.interceptors.request.use((config) => {
  // Attach Bearer token
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Forward the client timezone so the backend can resolve local times correctly
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) config.headers['x-client-timezone'] = tz;
  } catch (_) {
    // Ignore — backend falls back to UTC
  }

  // Activity tracking hook.
  // Callers that must NOT count as user activity (e.g. polling, /auth/me on mount)
  // pass  { skipActivityTracking: true }  in the axios request config.
  // This flag is client-side only — it is never serialised into HTTP headers
  // and has zero effect on CORS or network behaviour.
  if (!config.skipActivityTracking && typeof _activityResetter === 'function') {
    _activityResetter();
  }

  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor
// ---------------------------------------------------------------------------
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status === 401 && typeof window !== 'undefined') {
      // SESSION_EXPIRED comes from authMiddleware when the token's `iat` exceeds
      // the absolute session ceiling.  Route through AuthContext's deduplicated
      // logoutOnce instead of doing a raw redirect so cleanup, broadcasting, and
      // navigation happen exactly once regardless of how many concurrent requests
      // all receive a 401 at the same moment.
      if (
        error.response?.data?.code === 'SESSION_EXPIRED' &&
        typeof _sessionLogoutOnce === 'function'
      ) {
        _sessionLogoutOnce();
        return Promise.reject(error);
      }

      // All other 401s (invalid signature, UNAUTHENTICATED): original behaviour.
      try {
        localStorage.removeItem('token');
        clearAiStudioSessions();
      } catch (_) {}

      const isAuthLanding =
        window.location.pathname === '/' ||
        window.location.pathname.startsWith('/login');
      if (!isAuthLanding) {
        window.location.href = '/?auth=1';
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
